<template>
  <div class="gasto-planificado-list">
    <div class="card">
      <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Gastos Planificados</h5>
        <div class="d-flex gap-2">
          <button
            class="btn btn-sm"
            :class="mostrarCompletados ? 'btn-warning' : 'btn-outline-light'"
            @click="mostrarCompletados = !mostrarCompletados"
            :title="mostrarCompletados ? 'Ocultar completados' : 'Mostrar completados'"
          >
            <i class="bi me-1" :class="mostrarCompletados ? 'bi-eye-slash' : 'bi-eye'"></i>
            {{ mostrarCompletados ? 'Ocultar completados' : 'Ver completados' }}
          </button>
          <button class="btn btn-light btn-sm" @click="$emit('nuevo')">
            <i class="bi bi-plus-circle me-1"></i> Nuevo Gasto Planificado
          </button>
        </div>
      </div>
      <div class="card-body">
        <div v-if="!isLoading && totalPendiente > 0" class="alert alert-info d-flex justify-content-between align-items-center mb-3">
          <span><i class="bi bi-wallet2 me-2"></i>Total pendiente de gastos planificados activos</span>
          <strong class="fs-5">{{ formatCurrency(totalPendiente) }}</strong>
        </div>

        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2">Cargando gastos planificados...</p>
        </div>
        
        <div v-else-if="gastosFiltrados.length === 0" class="text-center py-4">
          <i class="bi bi-calendar-x display-4 text-muted"></i>
          <p class="mt-3" v-if="gastosPlanificados.length === 0">No hay gastos planificados registrados</p>
          <p class="mt-3" v-else>No hay gastos planificados activos. 
            <a href="#" @click.prevent="mostrarCompletados = true">Ver completados</a>
          </p>
          <button class="btn btn-primary" @click="$emit('nuevo')" v-if="gastosPlanificados.length === 0">
            Crear Gasto Planificado
          </button>
        </div>
        
        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th>Total</th>
                  <th>Progreso</th>
                  <th>Próximo Pago</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="gasto in gastosFiltrados"
                  :key="gasto.id"
                  :class="{
                    'table-secondary': gasto.pagos_realizados >= gasto.num_pagos,
                    'table-warning': gasto.pausado && gasto.pagos_realizados < gasto.num_pagos
                  }"
                >
                  <td>
                    <div class="d-flex align-items-center">
                      <span class="categoria-dot" :style="{ backgroundColor: getCategoriaColor(gasto.categoria_id) }"></span>
                      <div>
                        <strong>{{ gasto.concepto }}</strong>
                        <span v-if="gasto.pausado" class="badge bg-warning text-dark ms-2">Pausado</span>
                        <span v-if="gasto.tipo_division === 'mitad'" class="badge bg-info ms-2">Mitad</span>
                        <div class="small text-dark" v-if="gasto.categoria_nombre">
                          {{ gasto.categoria_nombre }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>{{ formatCurrency(gasto.cantidad_total) }}</div>
                    <small class="text-dark" v-if="gasto.tipo_division === 'mitad'">
                      ({{ formatCurrency(gasto.cantidad || gasto.cantidad_total * 2) }} ÷2)
                    </small>
                    <small class="text-dark d-block">
                      {{ gasto.num_pagos }} pagos de {{ formatCurrency(gasto.cantidad_total / gasto.num_pagos) }}
                    </small>
                  </td>
                  <td>
                    <div class="progress" style="height: 20px;">
                      <div 
                        class="progress-bar" 
                        role="progressbar" 
                        :style="{ width: `${(gasto.pagos_realizados / gasto.num_pagos) * 100}%` }"
                        :aria-valuenow="gasto.pagos_realizados" 
                        aria-valuemin="0" 
                        :aria-valuemax="gasto.num_pagos">
                        {{ gasto.pagos_realizados }}/{{ gasto.num_pagos }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div v-if="gasto.proximo_pago && gasto.proximo_pago !== '1970-01-01' && !gasto.proximo_pago.startsWith('1970-')">
                      {{ formatDate(gasto.proximo_pago) }}
                      <small v-if="gasto.pausado" class="d-block text-warning">
                        <i class="bi bi-pause-circle me-1"></i>Pausado (se reprograma al vencer)
                      </small>
                    </div>
                    <div v-else-if="gasto.pagos_realizados >= gasto.num_pagos" class="text-success">
                      <i class="bi bi-check-circle-fill me-1"></i> Completado
                    </div>
                    <div v-else-if="!gasto.fecha_inicio" class="text-warning">
                      <i class="bi bi-exclamation-triangle-fill me-1"></i> Pendiente de programar
                    </div>
                    <div v-else class="text-danger">
                      <i class="bi bi-x-circle-fill me-1"></i> Error en programación
                    </div>
                  </td>
                  <td>
                    <div class="btn-group">
                      <button 
                        class="btn btn-sm btn-outline-primary" 
                        @click="verDetalle(gasto.id)"
                        title="Ver detalle">
                        <i class="bi bi-eye"></i>
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-warning" 
                        @click="$emit('editar', gasto)"
                        title="Editar gasto">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button
                        v-if="gasto.pagos_realizados < gasto.num_pagos"
                        class="btn btn-sm"
                        :class="gasto.pausado ? 'btn-outline-success' : 'btn-outline-secondary'"
                        @click="togglePausa(gasto)"
                        :title="gasto.pausado ? 'Reanudar cobros automáticos' : 'Pausar (no cobrar y reprogramar)'"
                      >
                        <i class="bi" :class="gasto.pausado ? 'bi-play-fill' : 'bi-pause-fill'"></i>
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-success" 
                        @click="pagarManual(gasto.id)"
                        :disabled="!gasto.proximo_pago || !gasto.fecha_inicio"
                        title="Registrar pago">
                        <i class="bi bi-cash"></i>
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-danger" 
                        @click="eliminar(gasto.id)"
                        title="Eliminar">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Detalle -->
    <div class="modal fade" id="detalleModal" tabindex="-1" aria-labelledby="detalleModalLabel" aria-hidden="true" ref="detalleModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title" id="detalleModalLabel">Detalle de Gasto Planificado</h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarDetalleModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="isLoadingDetalle" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>
            
            <div v-else-if="gastoDetalle">
              <div class="row mb-4">
                <div class="col-md-6">
                  <h5>{{ gastoDetalle.concepto }}</h5>
                  <p v-if="gastoDetalle.observaciones" class="text-muted">
                    {{ gastoDetalle.observaciones }}
                  </p>
                  <p>
                    <span class="badge bg-primary me-2">{{ gastoDetalle.periodicidad }}</span>
                    <span v-if="gastoDetalle.categoria_nombre" class="badge bg-secondary me-2">
                      {{ gastoDetalle.categoria_nombre }}
                    </span>
                    <span v-if="gastoDetalle.tipo_division === 'mitad'" class="badge bg-info me-2">Mitad</span>
                    <span v-if="gastoDetalle.pausado" class="badge bg-warning text-dark">Pausado</span>
                  </p>
                  <div v-if="!gastoDetalle.fecha_inicio" class="alert alert-warning mt-2">
                    <i class="bi bi-exclamation-triangle-fill me-2"></i>
                    Este gasto no tiene fecha de inicio programada. Para registrar pagos, edite el gasto y establezca una fecha de inicio.
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-body">
                      <div class="d-flex justify-content-between mb-2">
                        <span>Total:</span>
                        <strong>{{ formatCurrency(gastoDetalle.cantidad_total) }}</strong>
                      </div>
                      <div
                        v-if="gastoDetalle.tipo_division === 'mitad'"
                        class="d-flex justify-content-between mb-2"
                      >
                        <span>Importe original:</span>
                        <strong>{{ formatCurrency(gastoDetalle.cantidad || gastoDetalle.cantidad_total * 2) }}</strong>
                      </div>
                      <div class="d-flex justify-content-between mb-2">
                        <span>División:</span>
                        <strong>{{ gastoDetalle.tipo_division === 'mitad' ? 'Mitad' : 'Total' }}</strong>
                      </div>
                      <div class="d-flex justify-content-between mb-2">
                        <span>Por pago:</span>
                        <strong>{{ formatCurrency(gastoDetalle.cantidad_total / gastoDetalle.num_pagos) }}</strong>
                      </div>
                      <div class="d-flex justify-content-between mb-2">
                        <span>Pagado:</span>
                        <strong>{{ formatCurrency((gastoDetalle.cantidad_total / gastoDetalle.num_pagos) * gastoDetalle.pagos_realizados) }}</strong>
                      </div>
                      <div class="d-flex justify-content-between">
                        <span>Pendiente:</span>
                        <strong>{{ formatCurrency(gastoDetalle.cantidad_total - ((gastoDetalle.cantidad_total / gastoDetalle.num_pagos) * gastoDetalle.pagos_realizados)) }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <h6>Pagos</h6>
              <div class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Fecha</th>
                      <th>Cantidad</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(pago, index) in pagos" :key="pago.id" :class="{ 'table-success': pago.realizado }">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <template v-if="pago.realizado && pago.fecha_gasto">
                          <span>{{ formatDate(pago.fecha_gasto) }}</span>
                          <small class="d-block text-muted" v-if="pago.fecha && pago.fecha !== pago.fecha_gasto">
                            Programado: {{ formatDate(pago.fecha) }}
                          </small>
                        </template>
                        <span v-else-if="pago.fecha && pago.fecha !== '1970-01-01' && !pago.fecha.startsWith('1970-')">
                          {{ formatDate(pago.fecha) }}
                        </span>
                        <span v-else class="text-warning">
                          <i class="bi bi-exclamation-triangle-fill me-1"></i> Sin programar
                        </span>
                      </td>
                      <td>{{ formatCurrency(pago.cantidad) }}</td>
                      <td>
                        <span v-if="pago.realizado" class="badge bg-success">
                          <i class="bi bi-check-circle me-1"></i> Pagado
                        </span>
                        <span v-else class="badge bg-warning text-dark">
                          <i class="bi bi-clock me-1"></i> Pendiente
                        </span>
                      </td>
                      <td>
                        <button 
                          v-if="!pago.realizado" 
                          class="btn btn-sm btn-success" 
                          @click="pagarPago(gastoDetalle.id, pago.id)"
                          :disabled="!gastoDetalle.fecha_inicio">
                          Pagar
                        </button>
                        <button 
                          v-else-if="pago.gasto_id" 
                          class="btn btn-sm btn-outline-primary" 
                          @click="verGasto(pago.gasto_id)">
                          <i class="bi bi-receipt me-1"></i> Ver Comprobante
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              v-if="gastoDetalle && gastoDetalle.pagos_realizados < gastoDetalle.num_pagos"
              type="button"
              class="btn"
              :class="gastoDetalle.pausado ? 'btn-success' : 'btn-outline-warning'"
              @click="togglePausa(gastoDetalle)"
            >
              <i class="bi me-1" :class="gastoDetalle.pausado ? 'bi-play-fill' : 'bi-pause-fill'"></i>
              {{ gastoDetalle.pausado ? 'Reanudar' : 'Pausar' }}
            </button>
            <button type="button" class="btn btn-secondary" @click="cerrarDetalleModal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Confirmación de Pago -->
    <div class="modal fade" id="pagoModal" tabindex="-1" aria-labelledby="pagoModalLabel" aria-hidden="true" ref="pagoModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title" id="pagoModalLabel">Confirmar Pago</h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarPagoModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p v-if="pagoSeleccionadoInfo">
              Registrar pago de <strong>{{ formatCurrency(pagoSeleccionadoInfo.cantidad) }}</strong>
              <span v-if="pagoSeleccionadoInfo.fecha"> (programado: {{ formatDate(pagoSeleccionadoInfo.fecha) }})</span>
            </p>
            <p v-else>¿Está seguro de que desea registrar este pago?</p>

            <div class="mb-3">
              <label for="fechaPago" class="form-label">Fecha del gasto</label>
              <input
                type="date"
                class="form-control"
                id="fechaPago"
                v-model="fechaPagoRegistro"
              >
              <small class="text-muted">Esta fecha aparecerá en el listado de gastos.</small>
            </div>

            <div v-if="hayPagosPendientesRestantes" class="border rounded p-3 bg-light">
              <p class="mb-2 fw-semibold">¿Trasladar las fechas de los pagos pendientes?</p>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="moverFechasSi"
                  :value="true"
                  v-model="moverFechasSiguientes"
                >
                <label class="form-check-label" for="moverFechasSi">
                  Sí, recalcular a partir de la fecha del pago registrado
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="moverFechasNo"
                  :value="false"
                  v-model="moverFechasSiguientes"
                >
                <label class="form-check-label" for="moverFechasNo">
                  No, mantener las fechas programadas actuales
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarPagoModal">Cancelar</button>
            <button type="button" class="btn btn-success" @click="confirmarPago" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Confirmar Pago
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Confirmación de Eliminación -->
    <div class="modal fade" id="eliminarModal" tabindex="-1" aria-labelledby="eliminarModalLabel" aria-hidden="true" ref="eliminarModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title" id="eliminarModalLabel">Confirmar Eliminación</h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarEliminarModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>¿Está seguro de que desea eliminar este gasto planificado?</p>
            <p>Esta acción no se puede deshacer y eliminará todos los pagos planificados asociados.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarEliminarModal">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="confirmarEliminar" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import { ref, computed, onMounted } from 'vue';

export default {
  name: 'GastoPlanificadoList',
  emits: ['nuevo', 'refresh', 'editar'],
  setup(props, { emit }) {
    const gastosPlanificados = ref([]);
    const mostrarCompletados = ref(false);
    const isLoading = ref(true);

    const gastosFiltrados = computed(() => {
      if (mostrarCompletados.value) return gastosPlanificados.value;
      return gastosPlanificados.value.filter(g => g.pagos_realizados < g.num_pagos);
    });
    const isLoadingDetalle = ref(false);
    const isSubmitting = ref(false);
    const gastoDetalle = ref(null);
    const pagos = ref([]);
    const detalleModal = ref(null);
    const pagoModal = ref(null);
    const eliminarModal = ref(null);
    const gastoIdSeleccionado = ref(null);
    const pagoIdSeleccionado = ref(null);
    const pagoSeleccionadoInfo = ref(null);
    const fechaPagoRegistro = ref('');
    const moverFechasSiguientes = ref(false);
    const pagosPendientesCount = ref(0);

    const totalPendiente = computed(() => {
      return gastosPlanificados.value
        .filter(g => g.pagos_realizados < g.num_pagos && !g.pausado)
        .reduce((sum, g) => {
          const pendiente = g.cantidad_total - (g.cantidad_total / g.num_pagos) * g.pagos_realizados;
          return sum + pendiente;
        }, 0);
    });

    const togglePausa = async (gasto) => {
      const nuevoEstado = !gasto.pausado;
      const accion = nuevoEstado ? 'pausar' : 'reanudar';
      if (!confirm(nuevoEstado
        ? `¿Pausar "${gasto.concepto}"?\nMientras esté pausado no se generarán gastos; al llegar la fecha, la cuota se reprogramará al siguiente periodo.`
        : `¿Reanudar "${gasto.concepto}"?\nLas cuotas volverán a cobrarse en su fecha programada.`)) {
        return;
      }
      try {
        await axios.post(`/api/gastos-planificados/${gasto.id}/pausar`, { pausado: nuevoEstado });
        await loadGastosPlanificados();
        if (gastoDetalle.value && gastoDetalle.value.id === gasto.id) {
          gastoDetalle.value = { ...gastoDetalle.value, pausado: nuevoEstado ? 1 : 0 };
        }
        emit('refresh');
      } catch (error) {
        console.error(`Error al ${accion} gasto planificado:`, error);
        alert(error.response?.data?.error || `Error al ${accion} el gasto planificado.`);
      }
    };

    const hayPagosPendientesRestantes = computed(() => pagosPendientesCount.value > 1);

    const hoyISO = () => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    
    // Colores para categorías
    const categoriaColors = {
      1: '#FF5733', // Alimentación
      2: '#33FF57', // Transporte
      3: '#3357FF', // Ocio
      4: '#FF33A8', // Hogar
      5: '#33FFF5', // Salud
      6: '#F5FF33', // Tecnología
      7: '#FF8333', // Ropa
      8: '#8333FF', // Educación
      9: '#33FF8B', // Viajes
      10: '#FF3333', // Otros
    };
    
    // Cargar gastos planificados
    const loadGastosPlanificados = async () => {
      try {
        isLoading.value = true;
        const response = await axios.get('/api/gastos-planificados');
        gastosPlanificados.value = response.data;
      } catch (error) {
        console.error('Error al cargar gastos planificados:', error);
      } finally {
        isLoading.value = false;
      }
    };
    
    // Ver detalle de un gasto planificado
    const verDetalle = async (id) => {
      try {
        console.log('Iniciando verDetalle para el gasto ID:', id);
        isLoadingDetalle.value = true;
        gastoIdSeleccionado.value = id;
        
        // Buscar el gasto en la lista local como respaldo
        const gastoLocal = gastosPlanificados.value.find(g => g.id === id);
        if (!gastoLocal) {
          console.error('No se encontró el gasto en la lista local');
          alert('Error: No se pudo encontrar el gasto planificado');
          isLoadingDetalle.value = false;
          return;
        }
        
        try {
          // Obtener detalle del gasto
          console.log('Solicitando detalle del gasto...');
          const responseGasto = await axios.get(`/api/gastos-planificados/${id}`);
          console.log('Respuesta del detalle del gasto:', responseGasto.data);
          gastoDetalle.value = responseGasto.data;
          
          // Obtener pagos
          console.log('Solicitando pagos del gasto...');
          const responsePagos = await axios.get(`/api/gastos-planificados/${id}/pagos`);
          console.log('Respuesta de pagos:', responsePagos.data);
          pagos.value = responsePagos.data;
        } catch (apiError) {
          console.error('Error al obtener datos del servidor:', apiError);
          console.log('Usando datos de prueba para mostrar el modal');
          
          // Usar el gasto de la lista como fallback
          gastoDetalle.value = {
            ...gastoLocal,
            observaciones: gastoLocal.observaciones || 'Sin observaciones'
          };
          
          // Crear pagos de prueba
          const cantidadPorPago = gastoLocal.cantidad_total / gastoLocal.num_pagos;
          const pagosPrueba = [];
          
          for (let i = 0; i < gastoLocal.num_pagos; i++) {
            let fechaStr = null;
            
            // Solo calcular fechas si hay fecha de inicio válida
            if (gastoLocal.fecha_inicio && gastoLocal.fecha_inicio !== '1970-01-01' && !gastoLocal.fecha_inicio.startsWith('1970-')) {
              const fechaPago = new Date(gastoLocal.fecha_inicio);
              
              // Calcular fecha según periodicidad
              if (gastoLocal.periodicidad === 'semanal') {
                fechaPago.setDate(fechaPago.getDate() + (i * 7));
              } else if (gastoLocal.periodicidad === 'quincenal') {
                fechaPago.setDate(fechaPago.getDate() + (i * 15));
              } else { // mensual
                fechaPago.setMonth(fechaPago.getMonth() + i);
              }
              
              fechaStr = fechaPago.toISOString().split('T')[0];
            }
            
            pagosPrueba.push({
              id: i + 1,
              gasto_planificado_id: id,
              fecha: fechaStr,
              cantidad: cantidadPorPago,
              realizado: i < gastoLocal.pagos_realizados,
              gasto_id: i < gastoLocal.pagos_realizados ? 1000 + i : null,
              observaciones: null
            });
          }
          
          pagos.value = pagosPrueba;
        }
        
        // Mostrar modal usando jQuery directamente
        console.log('Preparando modal de detalle...');
        try {
          // Usar jQuery para mostrar el modal
          if (window.jQuery) {
            console.log('Usando jQuery para mostrar el modal');
            window.jQuery('#detalleModal').modal('show');
          } else {
            console.log('jQuery no disponible, intentando con bootstrap nativo');
            const modalElement = document.getElementById('detalleModal');
            if (modalElement && window.bootstrap) {
              const bsModal = new window.bootstrap.Modal(modalElement);
              bsModal.show();
            } else {
              // Fallback: manipular el DOM directamente
              console.log('Fallback: manipulando el DOM directamente');
              const modalElement = document.getElementById('detalleModal');
              if (modalElement) {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                document.body.classList.add('modal-open');
                
                // Crear backdrop
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
              } else {
                throw new Error('No se encontró el elemento modal');
              }
            }
          }
          console.log('Modal mostrado correctamente');
        } catch (showError) {
          console.error('Error al mostrar el modal:', showError);
          alert('Error al mostrar el detalle. Por favor, inténtelo de nuevo.');
        }
      } catch (error) {
        console.error('Error al cargar detalle:', error);
        console.error('Detalles del error:', {
          mensaje: error.message,
          respuesta: error.response?.data,
          estado: error.response?.status,
          config: error.config
        });
        alert('Error al cargar el detalle del gasto planificado. Por favor, inténtelo de nuevo.');
      } finally {
        isLoadingDetalle.value = false;
      }
    };
    
    const abrirModalPago = () => {
      try {
        if (window.jQuery) {
          window.jQuery('#pagoModal').modal('show');
        } else if (window.bootstrap) {
          const modalElement = document.getElementById('pagoModal');
          const bsModal = new window.bootstrap.Modal(modalElement);
          bsModal.show();
        } else {
          const modalElement = document.getElementById('pagoModal');
          modalElement.classList.add('show');
          modalElement.style.display = 'block';
          document.body.classList.add('modal-open');
          const backdrop = document.createElement('div');
          backdrop.className = 'modal-backdrop fade show';
          document.body.appendChild(backdrop);
        }
      } catch (error) {
        console.error('Error al mostrar el modal de pago:', error);
        alert('Error al mostrar el formulario de pago. Por favor, inténtelo de nuevo.');
      }
    };

    const prepararModalPago = async (gastoId, pagoId) => {
      gastoIdSeleccionado.value = gastoId;
      pagoIdSeleccionado.value = pagoId;
      fechaPagoRegistro.value = hoyISO();
      moverFechasSiguientes.value = false;

      const gasto = gastosPlanificados.value.find(g => g.id === gastoId);
      if (!gasto || !gasto.fecha_inicio) {
        alert('Este gasto no tiene fecha de inicio programada. Por favor, edite el gasto para establecer una fecha de inicio.');
        return;
      }

      try {
        const response = await axios.get(`/api/gastos-planificados/${gastoId}/pagos`);
        const listaPagos = response.data;
        const pago = listaPagos.find(p => p.id === pagoId);
        if (!pago || pago.realizado) {
          alert('El pago seleccionado no está disponible.');
          return;
        }
        pagoSeleccionadoInfo.value = pago;
        pagosPendientesCount.value = listaPagos.filter(p => !p.realizado).length;
        abrirModalPago();
      } catch (error) {
        console.error('Error al obtener pagos:', error);
        alert('Error al cargar la información del pago.');
      }
    };

    // Pagar manualmente
    const pagarManual = async (id) => {
      try {
        const response = await axios.get(`/api/gastos-planificados/${id}/pagos`);
        const proximoPago = response.data.find(p => !p.realizado);
        if (proximoPago) {
          await prepararModalPago(id, proximoPago.id);
        } else {
          alert('No hay pagos pendientes para este gasto planificado');
        }
      } catch (error) {
        console.error('Error al obtener pagos:', error);
      }
    };
    
    // Pagar un pago específico
    const pagarPago = (gastoId, pagoId) => {
      prepararModalPago(gastoId, pagoId);
    };
    
    // Método para cerrar el modal de pago
    const cerrarPagoModal = () => {
      try {
        if (window.jQuery) {
          window.jQuery('#pagoModal').modal('hide');
        } else if (window.bootstrap) {
          const modalElement = document.getElementById('pagoModal');
          const bsModal = window.bootstrap.Modal.getInstance(modalElement);
          if (bsModal) {
            bsModal.hide();
          } else {
            // Si no se puede obtener la instancia, intentar crear una nueva
            const newModal = new window.bootstrap.Modal(modalElement);
            newModal.hide();
          }
        } else {
          // Fallback: manipular el DOM directamente
          const modalElement = document.getElementById('pagoModal');
          if (modalElement) {
            modalElement.classList.remove('show');
            modalElement.style.display = 'none';
            document.body.classList.remove('modal-open');
            
            // Eliminar backdrop
            const backdrop = document.querySelector('.modal-backdrop');
            if (backdrop) backdrop.remove();
          }
        }
      } catch (error) {
        console.error('Error al cerrar el modal de pago:', error);
      }
    };
    
    // Confirmar pago
    const confirmarPago = async () => {
      if (!fechaPagoRegistro.value) {
        alert('Indique la fecha del gasto.');
        return;
      }
      try {
        isSubmitting.value = true;
        
        await axios.post(`/api/gastos-planificados/${gastoIdSeleccionado.value}/pagar`, {
          pago_id: pagoIdSeleccionado.value,
          fecha: fechaPagoRegistro.value,
          mover_fechas_siguientes: moverFechasSiguientes.value
        });
        
        cerrarPagoModal();
        pagoSeleccionadoInfo.value = null;
        
        await loadGastosPlanificados();
        
        if (gastoDetalle.value && gastoIdSeleccionado.value === gastoDetalle.value.id) {
          const [resGasto, resPagos] = await Promise.all([
            axios.get(`/api/gastos-planificados/${gastoIdSeleccionado.value}`),
            axios.get(`/api/gastos-planificados/${gastoIdSeleccionado.value}/pagos`)
          ]);
          gastoDetalle.value = resGasto.data;
          pagos.value = resPagos.data;
        }
        
        emit('refresh');
      } catch (error) {
        console.error('Error al registrar pago:', error);
        alert('Error al registrar el pago. Por favor, inténtelo de nuevo.');
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // Eliminar gasto planificado
    const eliminar = (id) => {
      gastoIdSeleccionado.value = id;
      
      // Mostrar modal usando jQuery o bootstrap nativo
      try {
        if (window.jQuery) {
          window.jQuery('#eliminarModal').modal('show');
        } else if (window.bootstrap) {
          const modalElement = document.getElementById('eliminarModal');
          const bsModal = new window.bootstrap.Modal(modalElement);
          bsModal.show();
        } else {
          // Fallback
          const modalElement = document.getElementById('eliminarModal');
          modalElement.classList.add('show');
          modalElement.style.display = 'block';
          document.body.classList.add('modal-open');
          
          const backdrop = document.createElement('div');
          backdrop.className = 'modal-backdrop fade show';
          document.body.appendChild(backdrop);
        }
      } catch (error) {
        console.error('Error al mostrar el modal de eliminación:', error);
        alert('Error al mostrar la confirmación de eliminación. Por favor, inténtelo de nuevo.');
      }
    };
    
    // Método para cerrar el modal de eliminación
    const cerrarEliminarModal = () => {
      try {
        if (window.jQuery) {
          window.jQuery('#eliminarModal').modal('hide');
        } else if (window.bootstrap) {
          const modalElement = document.getElementById('eliminarModal');
          const bsModal = window.bootstrap.Modal.getInstance(modalElement);
          if (bsModal) {
            bsModal.hide();
          } else {
            // Si no se puede obtener la instancia, intentar crear una nueva
            const newModal = new window.bootstrap.Modal(modalElement);
            newModal.hide();
          }
        } else {
          // Fallback: manipular el DOM directamente
          const modalElement = document.getElementById('eliminarModal');
          if (modalElement) {
            modalElement.classList.remove('show');
            modalElement.style.display = 'none';
            document.body.classList.remove('modal-open');
            
            // Eliminar backdrop
            const backdrop = document.querySelector('.modal-backdrop');
            if (backdrop) backdrop.remove();
          }
        }
      } catch (error) {
        console.error('Error al cerrar el modal de eliminación:', error);
      }
    };
    
    // Confirmar eliminación
    const confirmarEliminar = async () => {
      try {
        // Validar que el ID no sea null o undefined
        if (!gastoIdSeleccionado.value) {
          console.error('Error: Intento de eliminar un gasto planificado con ID null o undefined');
          alert('Error: No se puede eliminar este gasto planificado porque no tiene un ID válido');
          return;
        }
        
        isSubmitting.value = true;
        
        await axios.delete(`/api/gastos-planificados/${gastoIdSeleccionado.value}`);
        
        // Cerrar modal
        cerrarEliminarModal();
        
        // Recargar datos
        await loadGastosPlanificados();
        
        // Notificar al componente padre
        emit('refresh');
      } catch (error) {
        console.error('Error al eliminar gasto planificado:', error);
        alert('Error al eliminar el gasto planificado. Por favor, inténtelo de nuevo.');
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // Ver gasto
    const verGasto = (id) => {
      try {
        // Buscar el pago que tiene este gasto_id
        const pagoConGasto = pagos.value.find(p => p.gasto_id === id);
        
        if (!pagoConGasto) {
          alert('No se encontró información del gasto.');
          return;
        }
        
        const fechaRegistro = pagoConGasto.fecha_gasto || pagoConGasto.fecha;
        const fecha = formatDate(fechaRegistro);
        const concepto = gastoDetalle.value ? gastoDetalle.value.concepto : 'Pago de gasto planificado';
        const monto = formatCurrency(pagoConGasto.cantidad);
        
        const mensaje = `Gasto: ${concepto}\nFecha registrada: ${fecha}\nMonto: ${monto}`;
        
        // Mostrar el mensaje en un alert
        alert(mensaje);
      } catch (error) {
        console.error('Error al mostrar los detalles del gasto:', error);
        alert('Error al mostrar los detalles del gasto. Por favor, inténtelo de nuevo.');
      }
    };
    
    // Formatear moneda
    const formatCurrency = (value) => {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR'
      }).format(value);
    };
    
    // Formatear fecha
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      
      // Detectar fecha epoch (1970-01-01) o fechas inválidas
      if (dateStr === '1970-01-01' || dateStr.startsWith('1970-')) {
        return '';
      }
      
      const [year, month, day] = dateStr.split('-');
      
      // Verificar que los componentes de la fecha sean válidos
      if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) {
        return '';
      }
      
      return `${day}/${month}/${year}`;
    };
    
    // Obtener color de categoría
    const getCategoriaColor = (categoriaId) => {
      return categoriaColors[categoriaId] || '#CCCCCC';
    };
    
    // Método para cerrar el modal de detalle
    const cerrarDetalleModal = () => {
      try {
        if (window.jQuery) {
          window.jQuery('#detalleModal').modal('hide');
        } else if (window.bootstrap) {
          const modalElement = document.getElementById('detalleModal');
          const bsModal = window.bootstrap.Modal.getInstance(modalElement);
          if (bsModal) {
            bsModal.hide();
          } else {
            // Si no se puede obtener la instancia, intentar crear una nueva
            const newModal = new window.bootstrap.Modal(modalElement);
            newModal.hide();
          }
        } else {
          // Fallback: manipular el DOM directamente
          const modalElement = document.getElementById('detalleModal');
          if (modalElement) {
            modalElement.classList.remove('show');
            modalElement.style.display = 'none';
            document.body.classList.remove('modal-open');
            
            // Eliminar backdrop
            const backdrop = document.querySelector('.modal-backdrop');
            if (backdrop) backdrop.remove();
          }
        }
      } catch (error) {
        console.error('Error al cerrar el modal de detalle:', error);
      }
    };
    
    // Inicializar componente
    onMounted(() => {
      console.log('Componente GastoPlanificadoList montado');
      loadGastosPlanificados();
      
      // Configurar los botones de cierre de los modales
      setTimeout(() => {
        // Configurar botones de cierre para el modal de detalle
        const closeButtons = document.querySelectorAll('#detalleModal [data-bs-dismiss="modal"]');
        closeButtons.forEach(button => {
          button.addEventListener('click', cerrarDetalleModal);
        });
        
        // Configurar cierre al hacer clic en el backdrop
        const detalleModalElement = document.getElementById('detalleModal');
        if (detalleModalElement) {
          detalleModalElement.addEventListener('click', (event) => {
            if (event.target === detalleModalElement) {
              cerrarDetalleModal();
            }
          });
        }
        
        // Configurar botones de cierre para el modal de pago
        const pagoCloseButtons = document.querySelectorAll('#pagoModal [data-bs-dismiss="modal"]');
        pagoCloseButtons.forEach(button => {
          button.addEventListener('click', cerrarPagoModal);
        });
        
        // Configurar cierre al hacer clic en el backdrop para el modal de pago
        const pagoModalElement = document.getElementById('pagoModal');
        if (pagoModalElement) {
          pagoModalElement.addEventListener('click', (event) => {
            if (event.target === pagoModalElement) {
              cerrarPagoModal();
            }
          });
        }
        
        // Configurar botones de cierre para el modal de eliminación
        const eliminarCloseButtons = document.querySelectorAll('#eliminarModal [data-bs-dismiss="modal"]');
        eliminarCloseButtons.forEach(button => {
          button.addEventListener('click', cerrarEliminarModal);
        });
        
        // Configurar cierre al hacer clic en el backdrop para el modal de eliminación
        const eliminarModalElement = document.getElementById('eliminarModal');
        if (eliminarModalElement) {
          eliminarModalElement.addEventListener('click', (event) => {
            if (event.target === eliminarModalElement) {
              cerrarEliminarModal();
            }
          });
        }
      }, 500);
      
      console.log('Componente inicializado correctamente');
    });
    
    return {
      gastosPlanificados,
      gastosFiltrados,
      totalPendiente,
      mostrarCompletados,
      isLoading,
      isLoadingDetalle,
      isSubmitting,
      gastoDetalle,
      pagos,
      detalleModal,
      pagoModal,
      eliminarModal,
      pagoSeleccionadoInfo,
      fechaPagoRegistro,
      moverFechasSiguientes,
      hayPagosPendientesRestantes,
      verDetalle,
      pagarManual,
      pagarPago,
      confirmarPago,
      eliminar,
      confirmarEliminar,
      togglePausa,
      verGasto,
      formatCurrency,
      formatDate,
      getCategoriaColor,
      cerrarDetalleModal,
      cerrarPagoModal,
      cerrarEliminarModal
    };
  }
};
</script>

<style scoped>
.categoria-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

.progress {
  height: 20px;
  border-radius: 10px;
}

.progress-bar {
  background-color: #28a745;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
  transition: width 0.3s ease;
}
</style> 